import { useGetPlaces } from "@api";
import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import { useCallback } from "react";
import { type FieldValues, type Path, type PathValue, useController } from "react-hook-form";
import { AsyncAutocomplete } from "./AsyncAutocomplete";
import type { IFormGenInputProps } from "./index";

export type TPlaceOption = { id: string; label: string };

export const FormGenInputPlaces = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  multiple,
}: IFormGenInputProps<Schema, T>) => {
  const [, getPlaces] = useGetPlaces();
  const { field, fieldState } = useController({
    control,
    name: uiSchemaElement.scope as unknown as Path<T>,
    defaultValue: (multiple ? [] : null) as PathValue<T, Path<T>>,
  });

  const fetchOptions = useCallback(
    async (query: string): Promise<TPlaceOption[]> => {
      const result = await getPlaces({ search: query });
      if (result.status !== 200) {
        return [];
      }
      return result.data.data.map((place) => ({ id: place._id, label: place.placeLabel }));
    },
    [getPlaces]
  );

  return (
    <AsyncAutocomplete<TPlaceOption>
      label={uiSchemaElement.label}
      multiple={multiple}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      name={field.name}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      fetchOptions={fetchOptions}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
    />
  );
};
